import React, { useState } from 'react';
import {
  usePermissions,
  useNotify,
  useRefresh,
  useDataProvider,
  useTranslate,
  useRedirect,
} from 'react-admin';
import {
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Typography,
  Alert,
} from '@mui/material';
import {
  MoreVert,
  Edit,
  Delete,
  Archive,
  Warning,
  Info,
  Assignment,
} from '@mui/icons-material';
import { AssignmentStatus, LockerAssignmentListItem } from '../types';
import AssignPhysicalLockers from './AssignPhysicalLockers';

interface LockerAssignmentActionsProps {
  record: LockerAssignmentListItem;
  hotelId?: string; // Añadir hotelId como prop opcional
}

interface ConfirmDialogProps {
  open: boolean;
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  onConfirm: () => void;
  onCancel: () => void;
  type?: 'delete' | 'archive' | 'info';
  children?: React.ReactNode;
}

const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  open,
  title,
  message,
  confirmText = 'Confirmar',
  cancelText = 'Cancelar',
  onConfirm,
  onCancel,
  type = 'info',
  children,
}) => {
  const getDialogColor = () => {
    switch (type) {
      case 'delete':
        return '#dc2626';
      case 'archive':
        return '#f59e0b';
      default:
        return '#0ea5e9';
    }
  };

  const getIcon = () => {
    switch (type) {
      case 'delete':
        return <Delete sx={{ fontSize: 28, color: '#dc2626' }} />;
      case 'archive':
        return <Archive sx={{ fontSize: 28, color: '#f59e0b' }} />;
      default:
        return <Info sx={{ fontSize: 28, color: '#0ea5e9' }} />;
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onCancel}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          boxShadow: '0 20px 25px -5px rgb(0 0 0 / 0.1)',
        },
      }}
    >
      <DialogTitle sx={{ 
        pb: 1,
        borderBottom: '1px solid #e5e7eb',
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          {getIcon()}
          <Typography variant="h6" sx={{ fontSize: '1.1rem', fontWeight: 600 }}>
            {title}
          </Typography>
        </Box>
      </DialogTitle>
      
      <DialogContent sx={{ pt: 3, pb: 2 }}>
        {type === 'delete' && (
          <Alert 
            severity="warning" 
            sx={{ mb: 2 }}
            icon={<Warning />}
          >
            Esta acción no se puede deshacer
          </Alert>
        )}
        
        <DialogContentText sx={{ 
          fontSize: '0.95rem',
          color: '#4b5563',
          lineHeight: 1.6,
          mb: children ? 2 : 0
        }}>
          {message}
        </DialogContentText>
        
        {children}
      </DialogContent>
      
      <DialogActions sx={{ 
        px: 3, 
        pb: 2.5,
        gap: 1 
      }}>
        <Button 
          onClick={onCancel}
          variant="outlined"
          sx={{
            borderColor: '#e5e7eb',
            color: '#6b7280',
            '&:hover': {
              borderColor: '#d1d5db',
              backgroundColor: '#f9fafb',
            },
          }}
        >
          {cancelText}
        </Button>
        <Button 
          onClick={onConfirm}
          variant="contained"
          sx={{
            backgroundColor: getDialogColor(),
            '&:hover': {
              backgroundColor: getDialogColor(),
              opacity: 0.9,
            },
          }}
        >
          {confirmText}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const LockerAssignmentActions: React.FC<LockerAssignmentActionsProps> = ({ record, hotelId }) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [dialogState, setDialogState] = useState<{
    open: boolean;
    type: 'delete' | 'archive' | null;
  }>({
    open: false,
    type: null,
  });
  
  // Estado para el diálogo de asignación de taquillas
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);

  const { permissions } = usePermissions();
  const notify = useNotify();
  const refresh = useRefresh();
  const dataProvider = useDataProvider();
  const translate = useTranslate();
  const redirect = useRedirect();
  
  const open = Boolean(anchorEl);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleClose = (event?: React.MouseEvent) => {
    event?.stopPropagation();
    setAnchorEl(null);
  };

  const role = permissions || localStorage.getItem('role');
  const canEdit = ['SuperAdmin', 'GroupAdmin', 'HotelAdmin', 'Receptionist'].includes(role);
  const canDelete = ['SuperAdmin', 'GroupAdmin', 'HotelAdmin'].includes(role);
  const canArchive = ['SuperAdmin', 'GroupAdmin', 'HotelAdmin', 'Receptionist'].includes(role);
  const canAssignLockers = ['SuperAdmin', 'GroupAdmin', 'HotelAdmin', 'Receptionist'].includes(role);

  const handleEdit = () => {
    handleClose();
    redirect('edit', 'locker-assignments', record.id);
  };

  const handleDeleteClick = () => {
    handleClose();
    setDialogState({ open: true, type: 'delete' });
  };

  const handleArchiveClick = () => {
    handleClose();
    setDialogState({ open: true, type: 'archive' });
  };

  const handleAssignLockersClick = () => {
    handleClose();
    
    console.log('=== handleAssignLockersClick ===');
    console.log('Hotel ID from props:', hotelId);
    console.log('Record:', record);
    
    if (!hotelId) {
      notify('Error: No se pudo determinar el ID del hotel', { type: 'error' });
      return;
    }
    
    setAssignDialogOpen(true);
  };

  const handleDialogCancel = () => {
    setDialogState({ open: false, type: null });
  };

  const handleDeleteConfirm = async () => {
    try {
      await dataProvider.delete('locker-assignments', {
        id: record.id,
        previousData: record,
      });
      notify(translate('resources.locker-assignments.messages.delete_success'), { type: 'success' });
      refresh();
    } catch (error) {
      notify(translate('resources.locker-assignments.messages.delete_error'), { type: 'error' });
    } finally {
      setDialogState({ open: false, type: null });
    }
  };

  const handleArchiveConfirm = async () => {
    try {
      // Usar el método específico de archivo del dataProvider
      await (dataProvider as any).archiveLockerAssignment(record.id);
      notify(translate('resources.locker-assignments.messages.archive_success'), { type: 'success' });
      refresh();
    } catch (error) {
      notify(translate('resources.locker-assignments.messages.archive_error'), { type: 'error' });
    } finally {
      setDialogState({ open: false, type: null });
    }
  };

  const getDialogProps = () => {
    const reference = record.displayReference || record.id;
    const guestInfo = record.guestName ? ` (${record.guestName})` : '';
    
    if (dialogState.type === 'delete') {
      return {
        title: translate('resources.locker-assignments.dialogs.delete_title', 'Eliminar Asignación'),
        message: translate(
          'resources.locker-assignments.dialogs.delete_message', 
          `¿Está seguro de que desea eliminar la asignación ${reference}${guestInfo}? Esta acción no se puede deshacer.`
        ),
        confirmText: translate('resources.locker-assignments.actions.delete', 'Eliminar'),
        cancelText: translate('ra.action.cancel', 'Cancelar'),
        onConfirm: handleDeleteConfirm,
        type: 'delete' as const,
      };
    }
    
    if (dialogState.type === 'archive') {
      const isActive = record.status === AssignmentStatus.Active;
      return {
        title: isActive 
          ? translate('resources.locker-assignments.dialogs.archive_active_title', 'Archivar y Liberar Asignación')
          : translate('resources.locker-assignments.dialogs.archive_title', 'Archivar Asignación'),
        message: isActive
          ? translate(
              'resources.locker-assignments.dialogs.archive_active_message', 
              `¿Está seguro de que desea archivar la asignación ${reference}${guestInfo}? Las taquillas serán liberadas y quedarán disponibles para nuevas asignaciones.`
            )
          : translate(
              'resources.locker-assignments.dialogs.archive_message', 
              `¿Está seguro de que desea archivar la asignación ${reference}${guestInfo}? La asignación se marcará como archivada.`
            ),
        confirmText: isActive 
          ? translate('resources.locker-assignments.actions.archive_and_release', 'Archivar y Liberar')
          : translate('resources.locker-assignments.actions.archive', 'Archivar'),
        cancelText: translate('ra.action.cancel', 'Cancelar'),
        onConfirm: handleArchiveConfirm,
        type: 'archive' as const,
      };
    }
    
    return {
      title: '',
      message: '',
      onConfirm: () => {},
      onCancel: handleDialogCancel,
    };
  };

  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Tooltip title={translate('ra.action.menu', 'Más opciones')}>
          <IconButton
            size={'small'}
            onClick={handleMenuOpen}
            sx={{ color: 'action.active' }}
          >
            <MoreVert fontSize={'small'} />
          </IconButton>
        </Tooltip>
      </Box>
      
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={() => handleClose()}
        onClick={(e) => e.stopPropagation()}
        slotProps={{
          paper: {
            sx: {
              boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1)',
              borderRadius: 1.5,
              minWidth: 180,
              '& .MuiMenuItem-root': {
                fontSize: '0.875rem',
                py: 1.25,
                '&:hover': {
                  backgroundColor: '#f3f4f6',
                },
              },
            },
          },
        }}
      >
        {canEdit && (
          <MenuItem onClick={handleEdit}>
            <ListItemIcon>
              <Edit fontSize="small" />
            </ListItemIcon>
            <ListItemText>{translate('ra.action.edit')}</ListItemText>
          </MenuItem>
        )}
        
        {/* Asignar taquillas - solo para pending */}
        {canAssignLockers && record.status === AssignmentStatus.Pending && (
          <MenuItem onClick={handleAssignLockersClick}>
            <ListItemIcon>
              <Assignment fontSize="small" />
            </ListItemIcon>
            <ListItemText>
              {translate('resources.locker-assignments.actions.assignLockers', 'Asignar Taquillas')}
            </ListItemText>
          </MenuItem>
        )}
        
        {/* Eliminar - solo para pending */}
        {canDelete && record.status === AssignmentStatus.Pending && (
          <MenuItem onClick={handleDeleteClick}>
            <ListItemIcon>
              <Delete fontSize="small"/>
            </ListItemIcon>
            <ListItemText>
              {translate('ra.action.delete')}
            </ListItemText>
          </MenuItem>
        )}

        {/* Archivar - para pending y active */}
        {canArchive && (record.status === AssignmentStatus.Pending || record.status === AssignmentStatus.Active) && (
          <MenuItem onClick={handleArchiveClick}>
            <ListItemIcon>
              <Archive fontSize="small"/>
            </ListItemIcon>
            <ListItemText>
              {record.status === AssignmentStatus.Active 
                ? translate('resources.locker-assignments.actions.archive_and_release', 'Archivar y Liberar')
                : translate('resources.locker-assignments.actions.archive', 'Archivar')
              }
            </ListItemText>
          </MenuItem>
        )}
      </Menu>

      {/* Diálogo de confirmación */}
      {dialogState.open && (
        <ConfirmDialog
          open={dialogState.open}
          onCancel={handleDialogCancel}
          {...getDialogProps()}
        />
      )}
      
      {/* Diálogo de asignación de taquillas */}
      <AssignPhysicalLockers
        open={assignDialogOpen}
        onClose={() => {
          setAssignDialogOpen(false);
          refresh(); // Refrescar después de cerrar por si se asignaron taquillas
        }}
        assignmentId={String(record.id)}
        hotelId={hotelId || ''}
      />
    </>
  );
};

export default LockerAssignmentActions;